import React from "react";
import { FiAlertTriangle, FiArrowDown, FiArrowUp, FiBookmark, FiExternalLink, FiLink } from "react-icons/fi";
import type { BCCADMINBaseModelsContentListItem, ContentTypeField } from "../../API/Models";
import { countLinks, countReferences, formatListFieldValue } from "./utils";
import { PREVIEW_SITE_URL, PRODUCTION_SITE_URL, buildPublicUrl, siteHost } from "../../Helper/siteLinks";
import ContentStatusPill, { deriveContentStatus } from "../../Components/ContentStatusPill";

/** Active sort state shared with the parent: a column token + direction. */
export interface ContentSort {
	field: string;
	desc: boolean;
}

export interface ContentListViewProps {
	items: BCCADMINBaseModelsContentListItem[];
	listFields: ContentTypeField[];
	showPathColumn: boolean;
	slugColumnLabel: string;
	totalColumnCount: number;
	/** When false the Title column is hidden entirely : the selected type has no title field, so a
	 * "Title" column would only ever show blanks. */
	hasTitleColumn: boolean;
	sort: ContentSort;
	onSort: (field: string) => void;
	resolveContentTypeLabel: (contentType?: string | null) => string;
	onNavigate: (item: BCCADMINBaseModelsContentListItem) => void;
}

/** Short localized date for the "Updated" column; tooltip carries the full timestamp. */
const formatUpdatedAt = (iso?: string): string => {
	if (!iso) return "-";
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return "-";
	return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
};

/** Per-row derived display data, computed once and shared by the table + card layouts. */
const getRowMeta = (p: BCCADMINBaseModelsContentListItem) => {
	const isPage = (p.contentType ?? "").toLowerCase() === "page";
	const decodedSlug =
		isPage && p.slug != null
			? (() => {
					try {
						return decodeURIComponent(p.slug);
					} catch {
						return p.slug;
					}
			  })()
			: "";
	const meta = (p.metadata ?? {}) as { description?: string };
	// Honest title: the record's own title, else (for pages) its slug. Records without a title field
	// get an empty string here : the caller renders the reference instead of a fake "Untitled".
	const title = p.title?.trim() || (isPage ? decodedSlug : "");
	const subtitle = meta.description ?? (isPage ? decodedSlug : p.language ?? "");
	const reference = (isPage ? decodedSlug : p.path || p.id || "") || "-";
	const dep = p as {
		previewUrls?: string[];
		productionUrls?: string[];
		isPreviewDeployed?: boolean;
		isProductionDeployed?: boolean;
	};
	const previewUrls = Array.isArray(dep.previewUrls) ? dep.previewUrls : [];
	const productionUrls = Array.isArray(dep.productionUrls) ? dep.productionUrls : [];
	// Deploy STATUS is the server's marker (stamped on EVERY publish path : UI, CLI,
	// hard-clean, promote), NOT a function of resolved link presence. A Slice-G record lives
	// in content_items with no page.json link row, so previewUrls can be empty while the record
	// is genuinely deployed : keying status off URL presence printed a false "Draft/Not deployed".
	// URL presence stays as a fallback for older content whose marker may not be stamped.
	const isProductionDeployed = dep.isProductionDeployed === true || productionUrls.length > 0;
	return {
		isPage,
		title,
		subtitle: subtitle || "",
		reference,
		previewUrls,
		productionUrls,
		isProductionDeployed,
		// Simplified honest state: the list payload carries the production flag but not the
		// production deploy timestamp, so this reads « En ligne » vs « Modifié » from the flag alone.
		status: deriveContentStatus({ isProductionDeployed }),
		linksCount: isPage ? countLinks(p) : null,
		refsCount: isPage ? countReferences(p) : null
	};
};

/** A clickable table header that toggles the sort on `field` and shows the active direction arrow. */
const SortHeader: React.FC<{
	field: string;
	label: string;
	sort: ContentSort;
	onSort: (field: string) => void;
	className?: string;
}> = ({ field, label, sort, onSort, className }) => {
	const active = sort.field === field;
	return (
		<th scope="col" className={`px-4 py-3 text-left ${className ?? ""}`}>
			<button
				type="button"
				onClick={() => onSort(field)}
				className="group inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-900"
				title={`Sort by ${label}`}
			>
				<span className="truncate">{label}</span>
				{active ? (
					sort.desc ? (
						<FiArrowDown className="h-3 w-3 text-slate-700" />
					) : (
						<FiArrowUp className="h-3 w-3 text-slate-700" />
					)
				) : (
					<FiArrowDown className="h-3 w-3 opacity-0 transition group-hover:opacity-40" />
				)}
			</button>
		</th>
	);
};

/**
 * Preview links built from the REAL deployed URLs (resolved from deployed-files) : a "Dev"
 * link per preview location, a "Prod" link per production location. A page live on preview
 * but not production shows no production link; not deployed anywhere → muted "Not deployed".
 * When the same content is live at several URLs (reachable from multiple dynamic pages),
 * every link is shown plus a warning.
 */
export const PreviewLinks: React.FC<{ previewUrls: string[]; productionUrls: string[] }> = ({ previewUrls, productionUrls }) => {
	const sky = "text-sky-700 border-sky-200 hover:bg-sky-50";
	const emerald = "text-emerald-700 border-emerald-200 hover:bg-emerald-50";
	// When the same content is live at several URLs, number the pills so each is distinguishable
	// but they stay compact and flow on the same line(s) instead of one tall stack per row.
	const links = [
		...previewUrls.map((url, i) => ({ label: previewUrls.length > 1 ? `Dev ${i + 1}` : "Dev", base: PREVIEW_SITE_URL, url, tone: sky })),
		...productionUrls.map((url, i) => ({ label: productionUrls.length > 1 ? `Prod ${i + 1}` : "Prod", base: PRODUCTION_SITE_URL, url, tone: emerald }))
	];
	if (links.length === 0) return <span className="text-xs text-slate-400">Not deployed</span>;
	const multi = previewUrls.length > 1 || productionUrls.length > 1;
	return (
		<div className="flex flex-wrap items-center gap-1">
			{links.map((l, i) => (
				<a
					key={`${l.label}-${i}`}
					href={buildPublicUrl(l.base, l.url)}
					target="_blank"
					rel="noopener noreferrer"
					onClick={(e) => e.stopPropagation()}
					title={`${siteHost(l.base)}/${l.url}`}
					className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold leading-none transition ${l.tone}`}
				>
					<FiExternalLink className="h-2.5 w-2.5 shrink-0" /> {l.label}
				</a>
			))}
			{multi && (
				<span
					title={`Même contenu déployé sur ${Math.max(previewUrls.length, productionUrls.length)} pages`}
					className="inline-flex items-center gap-0.5 rounded bg-amber-50 px-1 py-0.5 text-[10px] font-semibold leading-none text-amber-700"
				>
					<FiAlertTriangle className="h-2.5 w-2.5" /> ×{Math.max(previewUrls.length, productionUrls.length)}
				</span>
			)}
		</div>
	);
};

/**
 * Results list for the content browser. Purely presentational : data, sorting and pagination live
 * in the parent. Renders the same rows twice: a dense sortable table on lg+ screens and stacked
 * cards below that breakpoint, both fed by getRowMeta so the two layouts can never disagree on
 * titles, deploy status or preview links.
 */
const ContentListView: React.FC<ContentListViewProps> = ({
	items,
	listFields,
	showPathColumn,
	slugColumnLabel,
	hasTitleColumn,
	sort,
	onSort,
	resolveContentTypeLabel,
	onNavigate
}) => {
	if (items.length === 0) {
		return (
			<div className="rounded-2xl border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500 shadow-sm">
				No content found.
			</div>
		);
	}

	const referenceLabel = showPathColumn ? "Path / ID" : slugColumnLabel;

	return (
		<>
			{/* Desktop / wide: dense table. */}
			<div className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm lg:block">
				<table className="w-full table-fixed divide-y divide-slate-200 text-sm">
					<thead className="bg-slate-50">
						<tr>
							<SortHeader field="type" label="Type" sort={sort} onSort={onSort} className="w-[12%]" />
							{hasTitleColumn && <SortHeader field="title" label="Title" sort={sort} onSort={onSort} />}
							<SortHeader field="reference" label={referenceLabel} sort={sort} onSort={onSort} className="w-[18%]" />
							{listFields.map((field) => (
								<SortHeader
									key={field.key ?? field.title ?? "field"}
									field={`field:${field.key ?? ""}`}
									label={field.title?.trim() || field.key || "Field"}
									sort={sort}
									onSort={onSort}
									className="w-[12%]"
								/>
							))}
							<SortHeader field="updated" label="Updated" sort={sort} onSort={onSort} className="w-[120px]" />
							<th scope="col" className="w-[180px] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Preview</th>
							<SortHeader field="status" label="Status" sort={sort} onSort={onSort} className="w-[110px]" />
						</tr>
					</thead>
					<tbody className="divide-y divide-slate-100">
						{items.map((p) => {
							const m = getRowMeta(p);
							return (
								<tr
									key={`${p.contentType}-${p.slug ?? ""}-${p.id ?? ""}`}
									className="cursor-pointer align-top hover:bg-slate-50"
									onClick={() => onNavigate(p)}
								>
									<td className="px-4 py-3">
										<span className="inline-block max-w-full truncate rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
											{resolveContentTypeLabel(p.contentType)}
										</span>
									</td>
									{hasTitleColumn && (
										<td className="px-4 py-3">
											<p className="truncate font-semibold text-slate-900" title={m.title || m.reference}>
												{m.title || <span className="text-slate-400">{m.reference}</span>}
											</p>
											<p className="truncate text-xs text-slate-500" title={m.subtitle}>{m.subtitle || "-"}</p>
											{m.isPage && (
												<div className="mt-1 flex items-center gap-2 text-[11px] text-slate-500">
													<span className="inline-flex items-center gap-1"><FiLink className="text-slate-400" /> {m.linksCount}</span>
													<span className="inline-flex items-center gap-1"><FiBookmark className="text-slate-400" /> {m.refsCount}</span>
												</div>
											)}
										</td>
									)}
									<td className="px-4 py-3 font-mono text-xs text-slate-600">
										<span className="block truncate" title={m.reference}>{m.reference}</span>
									</td>
									{listFields.map((field) => {
										const fieldKey = field.key ?? "";
										const fieldValue = fieldKey ? (p.fields as Record<string, any> | undefined)?.[fieldKey] : null;
										const text = formatListFieldValue(fieldValue);
										return (
											<td key={`${p.id ?? p.slug ?? "row"}-${fieldKey}`} className="px-4 py-3 text-xs text-slate-600">
												<span className="block truncate" title={text}>{text}</span>
											</td>
										);
									})}
									<td className="px-4 py-3 text-xs text-slate-500">
										<span className="block truncate" title={p.updatedAt ?? ""}>{formatUpdatedAt(p.updatedAt)}</span>
									</td>
									<td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
										<PreviewLinks previewUrls={m.previewUrls} productionUrls={m.productionUrls} />
									</td>
									<td className="px-4 py-3">
										<ContentStatusPill status={m.status} />
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			{/* Narrow: stacked cards : every property visible, no horizontal scroll. */}
			<div className="space-y-3 lg:hidden">
				{items.map((p) => {
					const m = getRowMeta(p);
					return (
						<div
							key={`card-${p.contentType}-${p.slug ?? ""}-${p.id ?? ""}`}
							className="cursor-pointer rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-slate-300 hover:shadow"
							onClick={() => onNavigate(p)}
						>
							<div className="flex items-start justify-between gap-2">
								<span className="inline-block rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
									{resolveContentTypeLabel(p.contentType)}
								</span>
								<ContentStatusPill status={m.status} />
							</div>
							<p className="mt-2 font-semibold leading-snug text-slate-900">{m.title || m.reference}</p>
							{m.subtitle && <p className="mt-0.5 text-sm text-slate-500">{m.subtitle}</p>}

							<dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
								<div className="min-w-0">
									<dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{referenceLabel}</dt>
									<dd className="truncate font-mono text-xs text-slate-700" title={m.reference}>{m.reference}</dd>
								</div>
								<div className="min-w-0">
									<dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Updated</dt>
									<dd className="truncate text-xs text-slate-700" title={p.updatedAt ?? ""}>{formatUpdatedAt(p.updatedAt)}</dd>
								</div>
								{m.isPage && (
									<div>
										<dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Links / Refs</dt>
										<dd className="text-xs text-slate-700">{m.linksCount} links · {m.refsCount} refs</dd>
									</div>
								)}
								{listFields.map((field) => {
									const fieldKey = field.key ?? "";
									const fieldValue = fieldKey ? (p.fields as Record<string, any> | undefined)?.[fieldKey] : null;
									return (
										<div key={`card-${p.id ?? p.slug ?? "row"}-${fieldKey}`} className="min-w-0">
											<dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
												{field.title?.trim() || field.key || "Field"}
											</dt>
											<dd className="truncate text-xs text-slate-700">{formatListFieldValue(fieldValue)}</dd>
										</div>
									);
								})}
							</dl>

							<div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3" onClick={(e) => e.stopPropagation()}>
								<span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Preview</span>
								<PreviewLinks previewUrls={m.previewUrls} productionUrls={m.productionUrls} />
							</div>
						</div>
					);
				})}
			</div>
		</>
	);
};

export default ContentListView;
